import type { UserComment } from './user-comment/user-comment.interface';

/** Raw coordinates without provenance (wire/client input). */
export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/**
 * Geocoding source constants for address enrichment.
 * - AUTOCOMPLETE: address selected from provider autocomplete results.
 * - CLIENT: coordinates supplied directly by the client application.
 * - FRONT_OFFICE: address created/edited by a customer through the front office —
 *   locks the address against further API-driven overrides (order intake enrichment).
 * - KLAREO: enriched by the Klareo geocoding pipeline (HERE Maps).
 * - MANUAL: coordinates set manually by an operator.
 *
 * Note: CustomerAddress uses all five values; Site.location.coordinates.source is
 * narrowed to `'autocomplete' | 'manual'` only.
 */
export const GEOCODING_SOURCE = {
  AUTOCOMPLETE: 'autocomplete',
  CLIENT: 'client',
  FRONT_OFFICE: 'front_office',
  KLAREO: 'klareo',
  MANUAL: 'manual',
} as const;

export type GeocodingSource = (typeof GEOCODING_SOURCE)[keyof typeof GEOCODING_SOURCE];

/** Coordinates with geocoding provenance. Replaces the old {x, y} shape. */
export interface GeoCoordinates extends GeoPoint {
  score: number; // 0–1
  source: GeocodingSource;
}

export interface PostalAddress {
  additionalInformations?: string;
  city: string;
  complement?: string;
  country: string;
  digicode?: string;
  floor?: string;
  hasElevator?: boolean;
  houseNumber?: string;
  postalCode?: string;
  street?: string;
  /** Shared, tenant-wide driver comment on this address (site or customer address); last write wins. */
  userComment?: UserComment;
}

export interface Location {
  address: PostalAddress;
  coordinates: GeoCoordinates;
}

/**
 * Driver position recorded around a status-changing action (declare arrival, complete, fail).
 * `capturedAt` is when the underlying GPS fix was captured — when a last-known fix is used, this
 * can be up to ~60s before the driver's tap, and can also predate the server write time for
 * offline-queued actions replayed later.
 *
 * Trap: on `Task`/`Order` snapshots crossing HTTP/Service Bus, `capturedAt` travels as an ISO
 * string, not a `Date` instance — same trap as `Order.updatedAt`. See `RecordedCoordinatesInput`.
 */
export interface RecordedCoordinates extends GeoPoint {
  accuracy?: number | null;
  capturedAt: Date;
}

/** Wire shape of `RecordedCoordinates` — `capturedAt` travels as an ISO string over HTTP/Service Bus. */
export type RecordedCoordinatesInput = Omit<RecordedCoordinates, 'capturedAt'> & { capturedAt: string };

export interface TextHighlight {
  end: number;
  start: number;
}

export interface AutocompleteSuggestion {
  address?: {
    city?: string;
    countryCode?: string;
    countryName?: string;
    county?: string;
    district?: string;
    houseNumber?: string;
    label: string;
    postalCode?: string;
    state?: string;
    street?: string;
  };
  highlights?: {
    title?: TextHighlight[];
  };
  id: string;
  position?: GeoPoint;
  resultType: string;
  title: string;
}

export interface AutocompleteResponse {
  suggestions: AutocompleteSuggestion[];
}
